import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryButton, Modal } from "@fluentui/react";
import { Dismiss24Regular, Person24Filled, PersonInfo24Filled, Link24Filled, LinkAdd24Filled, LinkDismiss24Filled } from "@fluentui/react-icons";

import { storageService, unlinkAccount, UserLogin } from "../../api";

import googleIcon from "../../assets/icon-google.svg";
import microsoftIcon from "../../assets/icon-microsoft.svg";
import mailIcon from "../../assets/icon-mail.svg";

import styles from "./ProfileModal.module.css";

const REDIRECT_URI = `${import.meta.env.VITE_IMC_LINKING_URI}?responseCallback=${encodeURIComponent(document.location.origin)}&requestOrigin=IDM`;

interface Props {
    isProfileOpen: boolean;
    onClose: () => void;
    profile: UserLogin;
}

export const UserProfileModal: React.FC<Props> = ({ isProfileOpen, onClose, profile }: Props) => {
    const email = profile.linkedAccounts?.find(a => a.provider === "email");
    const microsoft = profile.linkedAccounts?.find(a => a.provider === "microsoft");
    const google = profile.linkedAccounts?.find(a => a.provider === "google");
    const canBeLinkedNewAccount = !(email && microsoft && google);

    const navigate = useNavigate();
    const [isLinkingModalOpen, setIsLinkingModalOpen] = useState(false);

    const processAccountUnlinking = useCallback(
        async (accountId: string) => {
            const result = await unlinkAccount(profile.userEmail, accountId);

            result.data.token = storageService.getToken()!;
            storageService.setProfile(result.data);
            navigate(0);
        },
        [profile?.userEmail, navigate]
    );

    const linkAccount = useCallback(
        (provider: string) => {
            if (profile.linkedAccounts?.find(a => a.provider === provider)) return;

            document.location.href = REDIRECT_URI + `&email=${encodeURIComponent(profile.userEmail)}&provider=${provider}`;
        },
        [profile]
    );

    return (
        <Modal isOpen={isProfileOpen} onDismiss={onClose} containerClassName={styles.profileModal}>
            <Modal isOpen={isLinkingModalOpen} onDismiss={() => setIsLinkingModalOpen(false)} containerClassName={styles.linkingModal}>
                <div className={styles.modalHeader}>
                    <Link24Filled />
                    <h3>Choose Account Type</h3>
                    <div onClick={() => setIsLinkingModalOpen(false)} className={styles.closeIcon}>
                        <Dismiss24Regular color="white" />
                    </div>
                </div>
                <div className={styles.linkingModalContent}>
                    <span>Select the type of account you want to link to your profile.</span>
                    <ul className={styles.linkAccountProviders}>
                        <div className={`${styles.linkButtonContent} ${email && styles.disabled}`} onClick={() => linkAccount("email")}>
                            <img src={mailIcon} className={styles.icon} />
                            <h4>Email</h4>
                            <small>Link Your Email</small>
                        </div>
                        <div className={`${styles.linkButtonContent} ${microsoft && styles.disabled}`} onClick={() => linkAccount("microsoft")}>
                            <img src={microsoftIcon} className={styles.icon} />
                            <h4>Microsoft Account</h4>
                            <small>Link your Microsoft or Azure&nbsp;account</small>
                        </div>
                        <div className={`${styles.linkButtonContent} ${google && styles.disabled}`} onClick={() => linkAccount("google")}>
                            <img src={googleIcon} className={styles.icon} />
                            <h4>Google Account</h4>
                            <small>Link your Google&nbsp;account</small>
                        </div>
                    </ul>
                </div>
            </Modal>
            <div className={styles.modalHeader}>
                <Person24Filled />
                <h3>User profile</h3>
                <div onClick={onClose} className={styles.closeIcon}>
                    <Dismiss24Regular color="white" />
                </div>
            </div>
            <div className={styles.body}>
                <div className={styles.modalBlock}>
                    <div className={styles.blockHeading}>
                        <PersonInfo24Filled />
                        <h4>Profile information</h4>
                    </div>
                    <ul className={styles.profileData}>
                        <li>
                            Email:
                            <br />
                            {profile.userProfile.email}
                        </li>
                        <li>
                            Authentication method:
                            <br />
                            {profile.userProfile.authMethod}
                        </li>
                        <li>
                            AccountStatus:
                            <br />
                            {profile.userProfile.status}
                        </li>
                        <li>
                            Last Login:
                            <br />
                            {profile.userProfile.lastLogin && new Date(profile.userProfile.lastLogin).toLocaleString()}
                        </li>
                    </ul>
                </div>
                <div className={styles.modalBlock}>
                    <div className={styles.blockHeading}>
                        <Link24Filled />
                        <h4>Linked accounts</h4>
                    </div>
                    <div className={styles.linkedAccountContent}>
                        <div className={styles.accountsDataHeader}>
                            <span>Manage your linked accounts</span>
                            {canBeLinkedNewAccount && (
                                <PrimaryButton type="button" onClick={() => setIsLinkingModalOpen(true)} className={styles.button}>
                                    <div className={styles.linkButtonContent}>
                                        <LinkAdd24Filled color="white" />
                                        <span>Link New Account</span>
                                    </div>
                                </PrimaryButton>
                            )}
                        </div>
                        <ul className={styles.accountsData}>
                            {email && (
                                <li className={styles.accountInfo}>
                                    <img src={mailIcon} className={styles.icon} />
                                    <div>
                                        <span>{email.email}</span>
                                        <span>{email.displayName} Account</span>
                                    </div>
                                    <PrimaryButton type="button" onClick={() => processAccountUnlinking(email.id)} className={styles.button}>
                                        <LinkDismiss24Filled color="white" />
                                    </PrimaryButton>
                                </li>
                            )}
                            {microsoft && (
                                <li className={styles.accountInfo}>
                                    <img src={microsoftIcon} className={styles.icon} />
                                    <div>
                                        <span>{microsoft.email}</span>
                                        <span>{microsoft.displayName} Account</span>
                                    </div>
                                    <PrimaryButton type="button" onClick={() => processAccountUnlinking(microsoft.id)} className={styles.button}>
                                        <LinkDismiss24Filled color="white" />
                                    </PrimaryButton>
                                </li>
                            )}
                            {google && (
                                <li className={styles.accountInfo}>
                                    <img src={googleIcon} className={styles.icon} />
                                    <div>
                                        <span>{google.email}</span>
                                        <span>{google.displayName} Account</span>
                                    </div>
                                    <PrimaryButton type="button" onClick={() => processAccountUnlinking(google.id)} className={styles.button}>
                                        <LinkDismiss24Filled color="white" />
                                    </PrimaryButton>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
            <div className={styles.modalBottom}>
                <PrimaryButton type="button" onClick={onClose} className={`${styles.button} ${styles.closeButton}`}>
                    Close
                </PrimaryButton>
            </div>
        </Modal>
    );
};
